import {useTranslation} from '@app/hooks/useTranslation';
import {Flex, HStack, Text, VStack} from '@tempo/core';
import {CheckCircleFilledIcon, CheckCircleOutlinedIcon} from '@tempo/icons';

const usePasswordLevel = (password: string): {level: string; color: string} => {
	const t = useTranslation();
	const mandatoryRegexP = [/.*[0-9]+.*/, /.*[a-zA-Z]+.*/, /^.{8,}$/];
	const regExps = [
		/.*[A-Z]+.*/,
		/.*[a-z]+.*/,
		/.*[0-9]+.*/,
		/.*[^A-Za-z0-9]+.*/,
	];

	const hasMinimalValue = mandatoryRegexP.reduce(
		(valid, regExp) => valid && regExp.test(password),
		true,
	);

	if (hasMinimalValue) {
		const passwordLevel = regExps.reduce(
			(level, regExp) => (level += regExp.test(password) ? 1 : 0),
			0,
		);

		if (passwordLevel > 3) {
			return {
				level: t('password.strong'),
				color: 'text.feedback.success.default',
			};
		}
		if (passwordLevel > 2) {
			return {
				level: t('password.medium'),
				color: 'text.feedback.warning.default',
			};
		}

		if (passwordLevel > 1) {
			return {
				level: t('password.weak'),
				color: 'text.feedback.error.default',
			};
		}

		return {
			level: t('password.tooweak'),
			color: 'text.feedback.error.default',
		};
	}
	return {
		level: t('password.tooweak'),
		color: 'text.feedback.error.default',
	};
};

export function PasswordLevelCnil({password}: {password: string}) {
	const t = useTranslation();
	const {level, color} = usePasswordLevel(password);
	if (!password) return null;
	const cnilRules = [
		{
			regex: /^.{8,}$/,
			text: t('password.atleastXchars', {
				count: 8,
				sprintf: ['8'],
			}),
		},
		{
			regex: /.*[a-zA-Z].*/,
			text: t('password.atleastaletter'),
		},
		{
			regex: /.*\d.*/,
			text: t('password.atleastanumber'),
		},
	];
	return (
		<Flex
			direction="row"
			justifyContent="space-between"
			borderRadius="s"
			py="spacing.m"
			mt="spacing.s"
			px="spacing.l"
			bg="background.neutral.secondary.default"
		>
			<VStack me="spacing.l" alignItems="flex-start">
				<Text
					variant="body.default.regular"
					color="text.neutral.primary.default"
				>
					{t('password.title')}
				</Text>
				{cnilRules.map((rule, i) => {
					const isValid = rule.regex.test(password);
					return (
						<HStack
							key={rule.text + i}
							color={
								isValid
									? 'text.feedback.success.default'
									: 'text.neutral.primary.default'
							}
							spacing="spacing.xs"
						>
							{isValid ? (
								<CheckCircleFilledIcon
									animation="300ms bounceScale"
									color="icon.feedback.success.default"
								/>
							) : (
								<CheckCircleOutlinedIcon color="icon.neutral.primary.disabled" />
							)}
							<span>{rule.text}</span>
						</HStack>
					);
				})}
			</VStack>
			<Text
				variant="body.default.regular"
				textAlign="end"
				flex="1"
				color={color}
			>
				{level}
			</Text>
		</Flex>
	);
}
